import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

/**
 * Confines keyboard focus inside the returned ref's element while `active`.
 * On activate:
 *   - moves focus to the first focusable child (or the container itself)
 *   - cycles Tab / Shift-Tab so focus never escapes
 * On deactivate:
 *   - restores focus to whatever was focused before activate
 *
 * Used by Modal. WCAG 2.4.3 (Focus Order) + WAI-ARIA APG dialog pattern.
 *
 * Returns: ref to attach to the trap container.
 */
export default function useFocusTrap(active) {
  const containerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    // Move focus into the container. Prefer an element marked
    // data-autofocus, else the first focusable, else the container.
    const initial =
      container.querySelector("[data-autofocus]") ||
      container.querySelector(FOCUSABLE_SELECTOR) ||
      container;
    initial.focus({ preventScroll: true });

    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const focusables = Array.from(
        container.querySelectorAll(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const current = document.activeElement;

      if (e.shiftKey && current === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus({ preventScroll: true });
      }
    };
  }, [active]);

  return containerRef;
}
